import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useCurrency } from '../../../context/CurrencyContext';

const { FiX, FiDollarSign, FiPlus, FiTrash2, FiPrinter, FiDownload, FiBriefcase } = FiIcons;

const ExpenseTrackerModal = ({ isOpen, onClose }) => {
  const { formatCurrency } = useCurrency();
  const [expenses, setExpenses] = useState([
    { id: 1, category: 'Service', item: 'Chapel Rental', amount: 1200, date: '2024-03-20', vendor: 'St. Jude Memorial' },
    { id: 2, category: 'Floral', item: 'White Lily Wreath', amount: 150, date: '2024-03-21', vendor: 'Bloom & Co' },
    { id: 3, category: 'Logistics', item: 'Premium Transportation', amount: 450, date: '2024-03-22', vendor: 'Legacy Limo' }
  ]);
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({ item: '', amount: '', category: 'Service', vendor: '' });

  const total = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  const handlePrint = () => window.print();

  const handleAddExpense = (e) => {
    e.preventDefault();
    if (!newItem.item || !newItem.amount) return;
    setExpenses([...expenses, { ...newItem, id: Date.now(), amount: parseFloat(newItem.amount), date: new Date().toISOString().split('T')[0] }]);
    setNewItem({ item: '', amount: '', category: 'Service', vendor: '' });
    setIsAdding(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      {/* PROFESSIONAL INVOICE PRINT VIEW */}
      <div className="hidden print:block fixed inset-0 bg-white z-[300] p-16 font-serif">
        <div className="flex justify-between items-start mb-20 border-b-8 border-accent-navy pb-10">
          <div>
            <h1 className="text-5xl font-bold text-accent-navy mb-2">OneFinalLegacy</h1>
            <p className="text-sm font-black uppercase tracking-[0.3em] text-accent-gold">Legacy Planning Statement</p>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold">INV-{(Date.now() % 100000).toString().padStart(6, '0')}</p>
            <p className="text-sm text-text-muted uppercase">Issue Date: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-20 mb-16">
          <div>
            <h4 className="text-xs font-black uppercase text-accent-gold tracking-widest mb-4">Billing Information</h4>
            <p className="text-lg font-bold">Estate of the Beloved</p>
            <p className="text-sm text-text-muted leading-relaxed">Digital Sanctuary Planning<br />San Francisco, CA 94103</p>
          </div>
          <div className="text-right">
            <h4 className="text-xs font-black uppercase text-accent-gold tracking-widest mb-4">Service Provider</h4>
            <p className="text-lg font-bold">OneFinalLegacy Concierge</p>
            <p className="text-sm text-text-muted">support@onefinelegacy.com<br />www.onefinelegacy.com</p>
          </div>
        </div>

        <table className="w-full mb-16">
          <thead>
            <tr className="bg-brand-light text-accent-navy border-y border-brand-dark">
              <th className="p-5 text-left text-xs font-black uppercase tracking-widest">Description</th>
              <th className="p-5 text-left text-xs font-black uppercase tracking-widest">Category</th>
              <th className="p-5 text-left text-xs font-black uppercase tracking-widest">Vendor</th>
              <th className="p-5 text-right text-xs font-black uppercase tracking-widest">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-dark">
            {expenses.map(exp => (
              <tr key={exp.id}>
                <td className="p-5">
                  <p className="font-bold text-lg">{exp.item}</p>
                  <p className="text-[10px] text-text-muted uppercase tracking-tighter">Date: {exp.date}</p>
                </td>
                <td className="p-5 text-sm uppercase font-medium">{exp.category}</td>
                <td className="p-5 text-sm">{exp.vendor || 'N/A'}</td>
                <td className="p-5 text-right font-bold text-lg">{formatCurrency(exp.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end pt-10">
          <div className="w-80 space-y-4">
            <div className="flex justify-between text-lg text-text-muted">
              <span>Subtotal</span>
              <span>{formatCurrency(total)}</span>
            </div>
            <div className="flex justify-between text-3xl font-bold text-accent-navy border-t-2 border-brand-dark pt-4">
              <span>Total Amount</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        <div className="mt-32 pt-10 border-t border-brand-dark text-center">
          <p className="text-sm italic text-text-muted mb-4">"A life well-lived is a legacy that never fades."</p>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-accent-gold">Official Memorial Record • OneFinalLegacy</p>
        </div>
      </div>

      {/* MODAL UI */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark"
      >
        <div className="p-8 md:p-12 border-b border-brand-light flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-gold text-white rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">Expense Tracker</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Professional Budget Management</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handlePrint} className="p-3 bg-brand-light text-accent-navy rounded-xl hover:bg-accent-navy hover:text-white transition-all flex items-center gap-2 group">
              <SafeIcon icon={FiPrinter} />
              <span className="text-[10px] font-black uppercase tracking-widest hidden md:inline">Generate Invoice</span>
            </button>
            <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted">
              <SafeIcon icon={FiX} />
            </button>
          </div>
        </div>

        <div className="p-8 overflow-y-auto max-h-[70vh]">
          <div className="grid grid-cols-2 gap-6 mb-8 text-center">
            <div className="bg-brand-light p-6 rounded-3xl border border-brand-dark">
              <p className="text-[10px] font-black uppercase text-text-muted mb-1 tracking-widest">Total Investment</p>
              <p className="text-4xl font-serif text-accent-navy">{formatCurrency(total)}</p>
            </div>
            <div className="bg-brand-light p-6 rounded-3xl border border-brand-dark">
              <p className="text-[10px] font-black uppercase text-text-muted mb-1 tracking-widest">Active Items</p>
              <p className="text-4xl font-serif text-accent-gold">{expenses.length}</p>
            </div>
          </div>

          <AnimatePresence>
            {isAdding && (
              <motion.form 
                initial={{ opacity: 0, height: 0 }} 
                animate={{ opacity: 1, height: 'auto' }} 
                onSubmit={handleAddExpense} 
                className="mb-8 p-6 bg-brand-light rounded-3xl space-y-4 border-2 border-accent-gold/20"
              >
                <div className="grid grid-cols-2 gap-4">
                  <input placeholder="Service/Item Name" className="px-4 py-3 rounded-xl border border-brand-dark text-sm" value={newItem.item} onChange={e => setNewItem({...newItem, item: e.target.value})} />
                  <input type="number" placeholder="Amount" className="px-4 py-3 rounded-xl border border-brand-dark text-sm" value={newItem.amount} onChange={e => setNewItem({...newItem, amount: e.target.value})} />
                  <input placeholder="Vendor Name" className="px-4 py-3 rounded-xl border border-brand-dark text-sm" value={newItem.vendor} onChange={e => setNewItem({...newItem, vendor: e.target.value})} />
                  <select className="px-4 py-3 rounded-xl border border-brand-dark text-sm" onChange={e => setNewItem({...newItem, category: e.target.value})}>
                    <option>Service</option>
                    <option>Logistics</option>
                    <option>Floral</option>
                    <option>Marketplace</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setIsAdding(false)} className="text-[10px] font-black uppercase text-text-muted">Cancel</button>
                  <button type="submit" className="bg-accent-gold text-white px-8 py-2 rounded-xl text-[10px] font-black uppercase">Add Entry</button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          <div className="space-y-4">
            {expenses.map((exp) => (
              <div key={exp.id} className="flex items-center justify-between p-6 bg-white border border-brand-dark rounded-3xl group hover:border-accent-gold transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-brand-light rounded-xl flex items-center justify-center text-accent-gold">
                    <SafeIcon icon={FiBriefcase} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-accent-navy">{exp.item}</p>
                    <p className="text-[9px] text-text-muted uppercase font-black tracking-widest">{exp.vendor} • {exp.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <span className="text-lg font-serif text-accent-navy">{formatCurrency(exp.amount)}</span>
                  <button onClick={() => setExpenses(expenses.filter(e => e.id !== exp.id))} className="text-text-muted hover:text-red-500 transition-all">
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {!isAdding && (
            <button onClick={() => setIsAdding(true)} className="w-full mt-8 py-5 bg-accent-navy text-white rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2">
              <SafeIcon icon={FiPlus} /> Log New Expense
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ExpenseTrackerModal;